import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiUser, FiArrowRight, FiMail, FiCheck, FiAlertCircle } from 'react-icons/fi';
import emailjs from '@emailjs/browser';

// Configuration EmailJS
const EMAILJS_SERVICE_ID = 'service_5a8t13o';
const EMAILJS_TEMPLATE_ID = 'template_i2aa6wr';
const EMAILJS_PUBLIC_KEY = 'zMoNd0pSIrPjf8bSK';

// Initialiser EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

const Blog = () => {
  // Blog posts data
  const posts = [
    {
      id: 1,
      title: 'Les meilleurs spots de windsurf en Méditerranée',
      excerpt: 'Découvrez notre sélection des plus beaux spots de windsurf en Méditerranée, idéaux pour tous les niveaux.',
      image: '/images/hero-bg.webp', // Placeholder, replace with actual image
      date: '15 Juin 2025',
      author: 'Marine Leroy',
      category: 'Destinations',
    },
    {
      id: 2,
      title: 'Comment choisir son équipement de windsurf',
      excerpt: 'Guide complet pour sélectionner le matériel adapté à votre niveau et aux conditions météorologiques.',
      image: '/images/galerie1.webp', // Placeholder, replace with actual image
      date: '2 Juin 2025',
      author: 'Thomas Dubois',
      category: 'Équipement',
    },
    {
      id: 3,
      title: 'Techniques avancées pour améliorer vos manœuvres',
      excerpt: 'Conseils d\'experts pour perfectionner votre technique et réaliser des figures impressionnantes.',
      image: '/images/hero-bg.webp', // Placeholder, replace with actual image
      date: '28 Mai 2025',
      author: 'Sophie Martin',
      category: 'Techniques',
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // État pour le formulaire de newsletter
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // 'idle', 'submitting', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Gestion de la soumission du formulaire de newsletter
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) return;
    
    setNewsletterStatus('submitting');
    
    try {
      console.log('Envoi via EmailJS Newsletter:', email);
      
      // Préparer les données pour EmailJS
      const templateParams = {
        from_name: 'Inscription Newsletter',
        from_email: email,
        phone: 'Non applicable',
        service: 'Inscription Newsletter',
        message: `Nouvelle inscription à la newsletter: ${email}`,
        to_email: 'contact@aiko-agency.fr'
      };
      
      // Envoi via EmailJS
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );
      
      console.log('Newsletter envoyée avec succès:', result);
      setNewsletterStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Erreur lors de l\'inscription à la newsletter:', error);
      setNewsletterStatus('error');
      setErrorMessage('Une erreur de connexion est survenue. Veuillez vérifier votre connexion internet et réessayer.');
    }
  };

  return (
    <section id="blog" className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-ocean-50 to-transparent"></div>
      
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span 
            className="inline-block px-4 py-2 rounded-full bg-ocean-100 text-ocean-800 text-sm font-medium mb-4"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Notre Blog
          </motion.span>
          
          <motion.h2 
            className="section-title text-gray-900 mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Conseils et <span className="text-ocean-600">actualités</span> windsurf
          </motion.h2>
          
          <motion.p 
            className="text-gray-600 text-lg"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Restez informés des dernières tendances, techniques et destinations pour votre prochaine aventure.
          </motion.p>
        </div>
        
        {/* Blog Posts Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {posts.map((post) => (
            <motion.article 
              key={post.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              variants={itemVariants}
            >
              {/* Featured Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
                <div className="absolute top-4 left-4 bg-ocean-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {post.category}
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 hover:text-ocean-600 transition-colors">
                  <a href="/blog/article">{post.title}</a>
                </h3>
                
                <p className="text-gray-600 mb-4">{post.excerpt}</p>
                
                {/* Meta */}
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <div className="flex items-center mr-4">
                    <FiCalendar className="mr-1" />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center">
                    <FiUser className="mr-1" />
                    <span>{post.author}</span>
                  </div>
                </div>
                
                <a 
                  href="/blog/article" 
                  className="inline-flex items-center text-ocean-600 font-medium hover:text-ocean-700 transition-colors"
                >
                  Lire la suite
                  <FiArrowRight className="ml-1" />
                </a>
              </div>
            </motion.article>
          ))}
        </motion.div>
        
        {/* View All Button */}
        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <a 
            href="/blog" 
            className="btn btn-primary"
          >
            Voir tous les articles
            <FiArrowRight className="ml-2" />
          </a>
        </motion.div>
        
        {/* Newsletter */}
        <motion.div 
          className="mt-20 p-8 rounded-2xl bg-ocean-50 border border-ocean-100"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Abonnez-vous à notre newsletter</h3>
              <p className="text-gray-600">
                Recevez nos derniers articles, conseils et offres spéciales directement dans votre boîte mail.
              </p>
            </div>
            <div>
              <form className="flex flex-col sm:flex-row gap-3" onSubmit={handleNewsletterSubmit}>
                <input 
                  type="email" 
                  placeholder="Votre email" 
                  className="flex-grow px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={newsletterStatus === 'submitting'}
                />
                <button 
                  type="submit" 
                  className={`btn ${newsletterStatus === 'submitting' ? 'btn-disabled' : 'btn-primary'} whitespace-nowrap`}
                  disabled={newsletterStatus === 'submitting'}
                >
                  {newsletterStatus === 'submitting' ? 'Envoi...' : 'S\'abonner'}
                </button>
              </form>
              
              {/* Message de succès ou d'erreur */}
              {newsletterStatus === 'success' && (
                <div className="mt-3 text-sm text-green-600 flex items-center">
                  <FiCheck className="mr-1" /> Merci pour votre inscription !
                </div>
              )}
              
              {newsletterStatus === 'error' && (
                <div className="mt-3 text-sm text-red-600 flex items-center">
                  <FiAlertCircle className="mr-1" /> {errorMessage}
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-2">
                En vous inscrivant, vous acceptez notre politique de confidentialité.
              </p>
            </div>
          </div>
        </motion.div>
        
      </div>
    </section>
  );
};

export default Blog;
